import {showNotification} from 'platform/components';

import {isNilOrEmpty, isNotNilOrEmpty} from 'ramda-adjunct';

import {
  UpdateMinipurchaseApiArg,
  useConvertForAllVehiclesMutation,
  useUpdateMinipurchaseMutation,
} from '@dms/api/finance';
import {useGetVehicleQuery} from '@dms/api/sales';
import {ConditionEnum, useLazyGetVehicleQuery} from '@dms/api/vehicle';
import {
  getFieldAvailabilityContextFromVehicle,
  useLazyGetAuthorizationProfilesAndCKKPermissions,
  useMandatoryFields,
  useMandatoryFieldsErrorDialog,
} from '@dms/shared';

import {getLastActiveBusinessCase} from '../utils/getLastActiveBusinessCase';
import {useHeadlessCloneVehicle} from './useHeadlessCloneVehicle';
import {useInterestData} from './useInterestData';

interface UseInterestToBusinessCaseParams {
  interestId: string;
  clonedVehicleMapRef: {current: Map<string, string>};
}

export function useInterestToBusinessCase(props: UseInterestToBusinessCaseParams) {
  const {isMandatoryFieldsEnabled} = useMandatoryFields();
  const {interest, miniSale, miniPurchase} = useInterestData(props.interestId);
  const {cloneVehicle} = useHeadlessCloneVehicle();
  const {handleSearchAuthorizationProfiles} = useLazyGetAuthorizationProfilesAndCKKPermissions();

  const [updateMinipurchase] = useUpdateMinipurchaseMutation();
  const [convertForAllVehicles] = useConvertForAllVehiclesMutation();
  const [getClonedVehicle] = useLazyGetVehicleQuery();

  const firstOfferedVehicleId = miniPurchase?.offeredVehiclesIds?.[0];
  const {data: firstVehicleData} = useGetVehicleQuery(
    {vehicleId: firstOfferedVehicleId ?? ''},
    {skip: isNilOrEmpty(firstOfferedVehicleId)}
  );

  const {isMandatoryFieldsError, handleMandatoryFieldsError} = useMandatoryFieldsErrorDialog(
    firstOfferedVehicleId,
    {interestId: props.interestId, isMiniSale: isNotNilOrEmpty(miniSale)}
  );

  const convertOnly = () => {
    handleSearchAuthorizationProfiles(
      {
        vehicleMake: firstVehicleData?.make ?? miniSale?.vehicleData?.make ?? '',
        vehicleType: firstVehicleData?.type ?? miniSale?.vehicleData?.type ?? null,
        vehicleModelFamily:
          firstVehicleData?.vehicle?.modelFamily ?? miniSale?.vehicleData?.modelFamily ?? null,
        vehicleModelFamilyGroup: firstVehicleData?.vehicle?.modelFamilyGroup ?? null,
      },
      interest?.customerId ?? undefined
    );

    return convertForAllVehicles({
      interestId: props.interestId,
      validateMandatoryFields: isMandatoryFieldsEnabled(
        (firstVehicleData?.vehicle?.condition as ConditionEnum) ?? undefined,
        getFieldAvailabilityContextFromVehicle(firstVehicleData)
      ),
    })
      .unwrap()
      .then((response) => {
        const lastActiveBusinessCase = getLastActiveBusinessCase(response.activeBusinessCases);

        if (!lastActiveBusinessCase) {
          showNotification.error();
          throw new Error('No active business case found in response');
        }

        return {businessCaseId: lastActiveBusinessCase.businessCaseId};
      });
  };

  const cloneAndConvert = async () => {
    const currentMiniPurchaseVehicleIds = miniPurchase?.offeredVehiclesIds ?? [];
    const alreadyClonedMap = props.clonedVehicleMapRef.current;
    const knownCloneIds = [...alreadyClonedMap.values()];

    const vehicleIdsToClone = currentMiniPurchaseVehicleIds.filter(
      (id) => !alreadyClonedMap.has(id) && !knownCloneIds.includes(id)
    );

    await Promise.all(
      vehicleIdsToClone.map(async (sourceVehicleId) => {
        const newVehicleId = await cloneVehicle(sourceVehicleId);
        alreadyClonedMap.set(sourceVehicleId, newVehicleId);
      })
    );

    const offeredVehiclesIdsPayload = currentMiniPurchaseVehicleIds.map(
      (id) => alreadyClonedMap.get(id) ?? id
    );

    if (vehicleIdsToClone.length > 0) {
      if (!miniPurchase?.id) {
        throw new Error('Cannot update minipurchase: missing minipurchase id');
      }

      const updateArg: UpdateMinipurchaseApiArg = {
        interestId: props.interestId,
        body: {
          ...miniPurchase,
          minipurchaseId: miniPurchase.id,
          offeredVehiclesIds: offeredVehiclesIdsPayload,
        },
      };

      await updateMinipurchase(updateArg).unwrap();
    }

    const firstClonedVehicle = await getClonedVehicle({
      vehicleId: offeredVehiclesIdsPayload[0],
    }).unwrap();

    handleSearchAuthorizationProfiles(
      {
        vehicleMake: firstClonedVehicle?.make ?? miniSale?.vehicleData?.make ?? '',
        vehicleType: firstClonedVehicle?.type ?? miniSale?.vehicleData?.type ?? null,
        vehicleModelFamily:
          firstClonedVehicle?.modelFamily ?? miniSale?.vehicleData?.modelFamily ?? null,
        vehicleModelFamilyGroup: firstClonedVehicle?.modelFamilyGroup ?? null,
      },
      interest?.customerId ?? undefined
    );

    const response = await convertForAllVehicles({
      interestId: props.interestId,
      validateMandatoryFields: false,
    }).unwrap();

    const lastActiveBusinessCase = getLastActiveBusinessCase(response.activeBusinessCases);

    if (!lastActiveBusinessCase) {
      showNotification.error();
      throw new Error('No active business case found in response');
    }

    return {businessCaseId: lastActiveBusinessCase.businessCaseId};
  };

  return {
    cloneAndConvert,
    convertOnly,
    isMandatoryFieldsError,
    handleMandatoryFieldsError,
  };
}
